import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AGENCY, SMS_CONSENT } from "@/data/content";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/site/Motion";

const empty = { name: "", email: "", phone: "", message: "", sms_opt_in: false, website: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", {
        ...form, type: "contact",
        consent_version: SMS_CONSENT.version,
        consent_text: SMS_CONSENT.text,
      });
      toast.success("Message sent! We'll be in touch soon.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-brand-sand/30">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Info */}
        <Reveal>
          <span className="overline">Get in touch</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink mt-4 leading-[1.02]">
            Come say hey, <span className="italic text-brand-clay">y'all.</span>
          </h2>
          <p className="mt-5 text-brand-moss leading-relaxed max-w-md">
            Stop by the office, give us a call, or send a note. A real person from Baton Rouge will get back to you.
          </p>

          <div className="mt-9 space-y-5">
            <InfoRow icon={Phone} label="Phone" value={AGENCY.phone} href={AGENCY.phoneHref} testid="contact-phone" />
            <InfoRow icon={Mail} label="Email" value={AGENCY.email} href={`mailto:${AGENCY.email}`} testid="contact-email" />
            <InfoRow icon={MapPin} label="Office" value={AGENCY.address}
              href={`https://maps.google.com/?q=${AGENCY.addressMapQuery}`} testid="contact-address" />
            <div className="flex gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-brand-green text-brand-cream shrink-0"><Clock className="h-5 w-5" /></span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-moss">Hours</div>
                {AGENCY.hours.map((h) => (
                  <div key={h.d} className="text-brand-ink text-sm mt-0.5"><span className="font-medium">{h.d}:</span> {h.h}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line h-56">
            <iframe
              title="Doug Jones Insurance and Financial Planning Agency location"
              className="h-full w-full grayscale-[0.2]"
              src={`https://maps.google.com/maps?q=${AGENCY.addressMapQuery}&z=14&output=embed`}
              loading="lazy"
            />
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form"
            className="rounded-2xl border border-brand-line bg-white p-7 sm:p-9">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
              value={form.website} onChange={(e) => set("website")(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <h3 className="font-serif text-3xl text-brand-ink">Send a message</h3>
            <p className="text-brand-moss text-sm mt-1 mb-6">We typically reply the same business day.</p>

            <div className="space-y-4">
              <Field label="Full name">
                <Input data-testid="contact-name-input" value={form.name}
                  onChange={(e) => set("name")(e.target.value)} placeholder="Jane Doe" className="bg-brand-cream/40 border-brand-line" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email">
                  <Input data-testid="contact-email-input" type="email" value={form.email}
                    onChange={(e) => set("email")(e.target.value)} placeholder="you@email.com" className="bg-brand-cream/40 border-brand-line" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="contact-phone-input" value={form.phone}
                    onChange={(e) => set("phone")(e.target.value)} placeholder="(225) 000-0000" className="bg-brand-cream/40 border-brand-line" />
                </Field>
              </div>
              <Field label="Message">
                <Textarea data-testid="contact-message-input" rows={4} value={form.message}
                  onChange={(e) => set("message")(e.target.value)} placeholder="How can we help?" className="bg-brand-cream/40 border-brand-line" />
              </Field>

              <label className="flex gap-3 items-start rounded-md border border-brand-line bg-brand-cream/40 p-3 cursor-pointer">
                <Checkbox data-testid="contact-sms-optin" checked={form.sms_opt_in}
                  onCheckedChange={(v) => set("sms_opt_in")(!!v)} className="mt-0.5" />
                <span className="text-xs leading-relaxed text-brand-moss">
                  I consent to receive SMS text messages from Doug Jones Insurance and Financial Planning Agency about my quote, policy, and
                  account. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out or HELP for help.
                  Consent is not a condition of purchase. See our{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-brand-green">Privacy Policy</a> and{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" className="underline text-brand-green">Terms of Service</a>.
                  <span className="block mt-1 font-mono uppercase tracking-wider text-[10px] text-brand-moss/70">A2P 10DLC compliant</span>
                </span>
              </label>

              <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                className="w-full rounded-full bg-brand-green text-brand-cream font-medium py-3.5 flex items-center justify-center gap-2 hover:bg-brand-ink active:scale-95 transition-all disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Send Message
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, href, testid }) {
  return (
    <a href={href} data-testid={testid} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
      className="flex gap-4 group">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-brand-green text-brand-cream shrink-0"><Icon className="h-5 w-5" /></span>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-moss">{label}</div>
        <div className="text-brand-ink font-medium mt-0.5 group-hover:text-brand-clay transition-colors">{value}</div>
      </div>
    </a>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-moss">{label}</span>
      {children}
    </div>
  );
}
